<template>
  <div class="plv-select" ref="root">
    <button
      type="button"
      ref="toggle"
      class="form-control plv-select-toggle"
      :id="id || undefined"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      v-on:click="toggleOpen"
      v-on:keydown="onToggleKeydown"
    >
      <ProductListViewIcon :view="currentOption.value" class="plv-select-toggle-icon" />
      <span class="plv-select-toggle-label">{{ currentOption.label }}</span>
      <i class="fa fa-fw fa-caret-down plv-select-caret" aria-hidden="true"></i>
    </button>
    <Teleport to="body">
      <ul
        v-if="open"
        ref="panel"
        class="plv-select-panel"
        role="listbox"
        tabindex="-1"
        :aria-activedescendant="optionId(highlighted)"
        v-on:keydown="onPanelKeydown"
      >
        <li
          v-for="(option, index) in options"
          :key="option.value"
          :id="optionId(index)"
          role="option"
          :aria-selected="option.value === selectedValue ? 'true' : 'false'"
          class="plv-select-option"
          :class="{ 'is-selected': option.value === selectedValue, 'is-highlighted': index === highlighted }"
          v-on:mouseenter="highlighted = index"
          v-on:click="select(option)"
        >
          <ProductListViewIcon :view="option.value" />
          <span class="plv-select-option-text">
            <strong>{{ option.label }}</strong>
            <small v-if="option.description">{{ option.description }}</small>
          </span>
          <i v-if="option.value === selectedValue" class="fa fa-fw fa-check plv-select-check" aria-hidden="true"></i>
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<script>
import { autoUpdate, computePosition, flip, offset, size } from '@floating-ui/dom'
import { productListViews } from '~/lib/productListViews'
import ProductListViewIcon from '~/components/ProductListViewIcon.vue'

let instanceCounter = 0

export default {
  name: 'ProductListViewSelector',
  components: {
    ProductListViewIcon
  },
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    },
    allowDefault: {
      type: Boolean,
      default: false
    }
  },
  data () {
    instanceCounter++
    return {
      uid: 'plv-' + instanceCounter,
      open: false,
      highlighted: 0,
      cleanupPosition: null
    }
  },
  computed: {
    selectedValue () {
      const value = this.modelValue && this.modelValue !== '0' ? String(this.modelValue) : ''
      if (!value && !this.allowDefault) {
        return 'default'
      }
      return value
    },
    options () {
      const options = []
      if (this.allowDefault) {
        options.push({ value: '', label: this._('Default'), description: this._('Use the storefront view of the category.') })
      }
      for (const view of productListViews) {
        options.push({ value: view.value, label: this._(view.label), description: this._(view.description) })
      }
      if (this.selectedValue && !options.some(o => o.value === this.selectedValue)) {
        options.push({ value: this.selectedValue, label: this.selectedValue, description: '' })
      }
      return options
    },
    currentOption () {
      return this.options.find(o => o.value === this.selectedValue) || this.options[0]
    }
  },
  beforeUnmount () {
    this.close(false)
  },
  methods: {
    optionId (index) {
      return this.uid + '-option-' + index
    },
    toggleOpen () {
      if (this.open) {
        this.close()
      } else {
        this.openPanel()
      }
    },
    openPanel () {
      if (this.open) {
        return
      }
      const index = this.options.findIndex(o => o.value === this.selectedValue)
      this.highlighted = index === -1 ? 0 : index
      this.open = true
      document.addEventListener('mousedown', this.onDocumentMousedown, true)
      this.$nextTick(() => {
        const panel = this.$refs.panel
        const toggle = this.$refs.toggle
        if (!panel || !toggle) {
          return
        }
        this.cleanupPosition = autoUpdate(toggle, panel, () => this.updatePosition(toggle, panel))
        panel.focus({ preventScroll: true })
        this.scrollHighlightedIntoView()
      })
    },
    close (focusToggle = true) {
      if (this.cleanupPosition) {
        this.cleanupPosition()
        this.cleanupPosition = null
      }
      document.removeEventListener('mousedown', this.onDocumentMousedown, true)
      if (!this.open) {
        return
      }
      this.open = false
      if (focusToggle && this.$refs.toggle) {
        this.$refs.toggle.focus()
      }
    },
    updatePosition (toggle, panel) {
      computePosition(toggle, panel, {
        placement: 'bottom-start',
        strategy: 'fixed',
        middleware: [
          offset(2),
          flip({ fallbackPlacements: ['top-start'], padding: 8 }),
          size({
            padding: 8,
            apply ({ availableHeight, rects, elements }) {
              Object.assign(elements.floating.style, {
                width: `${Math.max(rects.reference.width, 320)}px`,
                maxHeight: `${Math.min(420, Math.max(120, availableHeight))}px`
              })
            }
          })
        ]
      }).then(({ x, y }) => {
        Object.assign(panel.style, { left: `${x}px`, top: `${y}px` })
      })
    },
    onDocumentMousedown (event) {
      const root = this.$refs.root
      const panel = this.$refs.panel
      if ((root && root.contains(event.target)) || (panel && panel.contains(event.target))) {
        return
      }
      this.close(false)
    },
    select (option) {
      this.$emit('update:modelValue', option.value)
      this.close()
    },
    onToggleKeydown (event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        this.openPanel()
      }
    },
    onPanelKeydown (event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          this.highlighted = Math.min(this.options.length - 1, this.highlighted + 1)
          this.scrollHighlightedIntoView()
          break
        case 'ArrowUp':
          event.preventDefault()
          this.highlighted = Math.max(0, this.highlighted - 1)
          this.scrollHighlightedIntoView()
          break
        case 'Home':
          event.preventDefault()
          this.highlighted = 0
          this.scrollHighlightedIntoView()
          break
        case 'End':
          event.preventDefault()
          this.highlighted = this.options.length - 1
          this.scrollHighlightedIntoView()
          break
        case 'Enter':
        case ' ':
          event.preventDefault()
          if (this.options[this.highlighted]) {
            this.select(this.options[this.highlighted])
          }
          break
        case 'Escape':
          event.preventDefault()
          event.stopPropagation()
          this.close()
          break
        case 'Tab':
          this.close()
          break
      }
    },
    scrollHighlightedIntoView () {
      this.$nextTick(() => {
        const el = document.getElementById(this.optionId(this.highlighted))
        if (el) {
          el.scrollIntoView({ block: 'nearest' })
        }
      })
    }
  }
}
</script>

<style scoped>
.plv-select {
  position: relative;
  width: 100%;
}
.plv-select-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  height: auto;
  min-height: 34px;
  padding: 4px 8px;
  text-align: left;
  cursor: pointer;
}
.plv-select-toggle-icon {
  width: 40px;
  height: 25px;
}
.plv-select-toggle-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plv-select-caret {
  flex: 0 0 auto;
  color: #777;
}
.plv-select-panel {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 3000;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  outline: none;
}
.plv-select-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  cursor: pointer;
}
.plv-select-option.is-highlighted {
  background: #f2f6f9;
}
.plv-select-option.is-selected strong {
  color: #2a9fd6;
}
.plv-select-option-text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  line-height: 1.35;
}
.plv-select-option-text small {
  color: #777;
  white-space: normal;
}
.plv-select-check {
  flex: 0 0 auto;
  color: #2a9fd6;
}
</style>
